// src/components/AppContent.js

import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { NotificationContext } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import Sidebar from './Sidebar';
import Footer from './Footer';
import BottomNav from './BottomNav';
import NotificationModal from './NotificationModal';
import { ToastContainer } from 'react-toastify';
import classNames from 'classnames';
import { presenceTracker } from '../utils/presenceTracker';
import { useLocation } from 'react-router-dom';

const AppContent = ({ children }) => {
  const { currentUser, authReady } = useContext(AuthContext);
  const { 
    isModalOpen, 
    notifications, 
    closeModal, 
    markNotificationAsRead, 
    loading: notificationsLoading 
  } = useContext(NotificationContext);
  const { isDarkMode } = useTheme();
  const [showBanner, setShowBanner] = useState(true);
  const [bannerReady, setBannerReady] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (authReady && currentUser) {
      presenceTracker.startTracking();
    }
  }, [authReady, currentUser]);

  // Hide banner completely on auth pages, show immediately for other pages
  useEffect(() => {
    if (['/login', '/register'].includes(location.pathname)) {
      // Never show banner on auth pages
      setBannerReady(false);
    } else {
      // Show banner immediately for other pages
      setBannerReady(true);
    }
  }, [location.pathname]);

  // Define common class names
  const backgroundClass = isDarkMode
    ? 'bg-background-dark text-text-dark'
    : 'bg-background-light text-text-light';

  // Component-level CSS custom properties for dynamic theming
  const componentStyle = {
    '--tw-bg-opacity': '0.85',
    '--bullet-color': isDarkMode ? '#F9FAFB' : '#1F2937', // Theme-aware bullet color
    
    /* 
     * COMPONENT COLOR PALETTE
     * These override the global theme colors for specific components
     * Updated to match the new dark theme (gray-900 background)
     */
    
    // PRIMARY COLORS - Main backgrounds
    '--primary-light': '#FFFFFF',         // Cards, panels in light mode
    '--primary-dark': '#1F2937',          // Cards, panels in dark mode (gray-800)
    
    // SECONDARY COLORS - Subtle backgrounds  
    '--secondary-light': '#F8FAFC',       // Input fields, hover states in light mode
    '--secondary-dark': '#374151',        // Input fields, hover states in dark mode (gray-700)
    
    // ACCENT COLORS - Interactive elements
    '--accent-light': '#009AA1',          // Slightly darker teal for better contrast
    '--accent-dark': '#0097B2',           // Teal for dark mode
    '--accent-secondary-light': '#FF6B6B', // Warning/error red
    '--accent-secondary-dark': '#E05252',   // Darker red for dark mode
    '--accent-tertiary-light': '#4ECDC4',   // Success/info teal
    '--accent-tertiary-dark': '#38B2A8',    // Darker teal for dark mode
    
    // TEXT COLORS
    '--text-light': '#1F2937',            // Dark gray text (improved contrast)
    '--text-dark': '#F9FAFB',             // Very light text for dark mode (gray-50)
    
    // BACKGROUND COLORS
    '--background-light': '#FFFFFF',      // App background light mode
    '--background-dark': '#111827',       // App background dark mode (matches login)
    
    /* OLD DARK THEME BACKUP
     * '--primary-dark': '#1A1A1D',
     * '--secondary-dark': '#2C2C2C', 
     * '--text-dark': '#F0F0F0',
     * '--background-dark': '#121212',
     */
    // New surface colors for cards/panels
    '--surface-light': '#F8F8F8',
    '--surface-dark': '#252525',
    // Status colors
    '--success-light': '#4CAF50',
    '--success-dark': '#3B8A3F',
    '--error-light': '#F44336',
    '--error-dark': '#D32F2F',
    '--warning-light': '#FFC107',
    '--warning-dark': '#FFA000'
  };

  // Determine anchor position based on screen size (example logic)
  const [anchorPos, setAnchorPos] = useState('desktop');
  useEffect(() => {
    const checkSize = () => {
      setAnchorPos(window.innerWidth < 768 ? 'mobile' : 'desktop');
    };
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  return (
    <div className="relative flex flex-col min-h-screen overflow-x-hidden">
      {/* Show the top banner when ready */}
      {showBanner && bannerReady && (
        <div className="relative isolate flex items-center gap-x-6 overflow-hidden bg-background-light dark:bg-background-dark px-2 py-1.5 sm:py-2.5 sm:px-3.5 sm:before:flex-1 fixed top-0 left-0 right-0 z-30">
          {/* First gradient background */}
          <div className="absolute left-[max(-7rem,calc(50%-52rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl" aria-hidden="true">
            <div 
              className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-accent-light to-accent-dark opacity-30 dark:opacity-20" 
              style={{clipPath: "polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)"}}
            ></div>
          </div>
          {/* Second gradient background */}
          <div className="absolute left-[max(45rem,calc(50%+8rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl" aria-hidden="true">
            <div 
              className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-accent-light to-accent-dark opacity-30 dark:opacity-20" 
              style={{clipPath: "polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)"}}
            ></div>
          </div>
          {/* Content */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 sm:gap-y-2">
            <p className="text-sm leading-tight sm:leading-6 text-text-light dark:text-text-dark">
              <strong className="font-semibold">Demo Version</strong>
              <svg viewBox="0 0 2 2" className="mx-2 inline h-0.5 w-0.5 fill-current" aria-hidden="true">
                <circle cx="1" cy="1" r="1" />
              </svg>
              For Demonstration Purposes Only 🚀
            </p>
          </div>
          {/* Close button */}
          <div className="flex flex-1 justify-end">
            <button 
              type="button" 
              className="-m-3 p-3 focus-visible:outline-offset-[-4px] text-text-light dark:text-text-dark hover:opacity-80 transition-opacity"
              onClick={() => setShowBanner(false)}
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Show the sidebar on desktop for all routes except /login and /register */}
      {!['/login', '/register'].includes(location.pathname) && (
        <div className="hidden md:block relative z-40">
          <Sidebar />
        </div>
      )}

      {/* Main Content Area or Login/Register Page */}
      {['/login', '/register'].includes(location.pathname) ? (
        <div className="flex flex-col items-center justify-between w-full h-full" style={{ minHeight: '100vh' }}>
          {children}
          {/* Footer at the bottom for login/register page */}
          <Footer />
        </div>
      ) : (
        <>
          {/* Main Content Area and rest of layout for other routes */}
          <div
            className={classNames(
              'flex-1',
              showBanner && bannerReady ? 'pt-2 sm:pt-14 md:pt-16' : '',
              backgroundClass
            )}
          >
            <div className="flex flex-grow relative">
              {/* Sidebar - Only visible on desktop (already rendered above) */}
              {/* Main content wrapper */}
              <div className="flex-grow flex flex-col">
                {/* Main content */}
                <main className="flex-grow relative pb-16 md:pb-0">
                  <div 
                    className={classNames('p-4 relative', backgroundClass)}
                    style={componentStyle}
                  >
                    <div className="max-w-3xl mx-auto">
                      {children}
                    </div>
                  </div>
                </main>
              </div>
            </div>

            {/* Fixed Bottom Navigation for Mobile */}
            <div className="fixed bottom-0 left-0 right-0 z-[100]">
              <BottomNav />
            </div>

            {/* Footer - Hide on mobile */}
            <div className="hidden md:block">
              <Footer />
            </div>
          </div>
        </>
      )}

      {/* --- Render Notification Modal Conditionally --- */}
      {!notificationsLoading && (
        <NotificationModal
          isOpen={isModalOpen}
          notifications={notifications}
          onClose={closeModal}
          onMarkAsRead={markNotificationAsRead}
          anchorPosition={anchorPos}
          style={componentStyle}
        />
      )}
      {/* --- End Render Modal --- */}

      {/* Toast Container with highest z-index */}
      <ToastContainer
        position="top-right"
        autoClose={1700}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
        className="!fixed !top-0 !right-0 !left-auto !mt-8 !mr-4 !z-[110]"
        toastClassName={`
          text-[4px] sm:text-sm
          p-0.5 sm:p-3
          w-[250px] sm:w-[300px]
          min-h-[20px] sm:min-h-[48px]
          ${isDarkMode 
            ? 'bg-primary-dark/40 text-text-dark border-secondary-dark/20' 
            : 'bg-primary-light/40 text-text-light border-secondary-light/20'
          } 
          border rounded-lg shadow-lg
          backdrop-blur-sm
          transition-all duration-300 ease-in-out
          hover:bg-opacity-50
          hover:backdrop-blur-md
          !ml-auto
        `}
        bodyClassName="text-[4px] sm:text-sm font-medium"
        progressClassName={`${isDarkMode ? 'bg-accent-dark/60' : 'bg-accent-light/60'} h-[1px] sm:h-1.5 rounded-full`}
        style={{
          '--toastify-icon-color-success': isDarkMode ? 'rgba(34, 197, 94, 0.7)' : 'rgba(22, 163, 74, 0.7)',
          '--toastify-icon-color-error': isDarkMode ? 'rgba(239, 68, 68, 0.7)' : 'rgba(220, 38, 38, 0.7)',
          '--toastify-icon-color-warning': isDarkMode ? 'rgba(245, 158, 11, 0.7)' : 'rgba(217, 119, 6, 0.7)',
          '--toastify-icon-color-info': isDarkMode ? 'rgba(59, 130, 246, 0.7)' : 'rgba(37, 99, 235, 0.7)',
          '--toastify-text-color-light': isDarkMode ? 'rgba(226, 232, 240, 0.9)' : 'rgba(30, 41, 59, 0.9)',
          '--toastify-text-color-dark': isDarkMode ? 'rgba(226, 232, 240, 0.9)' : 'rgba(30, 41, 59, 0.9)',
          '--toastify-color-progress-success': isDarkMode ? 'rgba(34, 197, 94, 0.6)' : 'rgba(22, 163, 74, 0.6)',
          '--toastify-color-progress-error': isDarkMode ? 'rgba(239, 68, 68, 0.6)' : 'rgba(220, 38, 38, 0.6)',
          '--toastify-color-progress-warning': isDarkMode ? 'rgba(245, 158, 11, 0.6)' : 'rgba(217, 119, 6, 0.6)',
          '--toastify-color-progress-info': isDarkMode ? 'rgba(59, 130, 246, 0.6)' : 'rgba(37, 99, 235, 0.6)',
          '--toastify-font-family': 'inherit',
          '--toastify-toast-background': 'transparent',
          '--toastify-toast-width': '300px',
          '--toastify-toast-min-height': '48px',
          '--toastify-z-index': '50',
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          left: 'auto',
          transform: 'none',
          pointerEvents: 'none',
        }}
        toastStyle={{ pointerEvents: 'auto' }}
      />
    </div>
  );
};

export default AppContent;
